import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Loader2 } from "lucide-react";

const BG_IMG = "https://images.unsplash.com/photo-1768523974097-b7717b2d8363?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxkb2clMjByZWxheGluZyUyMGhvbWV8ZW58MHx8fHwxNzc1OTM4MjYyfDA&ixlib=rb-4.1.0&q=85&w=800";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) { navigate("/dashboard"); return null; }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-[calc(100vh-64px)] flex">
      {/* Left, form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-10 lg:py-16">
        <div className="w-full max-w-[400px]">
          <div className="mb-10">
            <h1 className="font-outfit font-bold text-3xl text-paw-text tracking-tight mb-2">Welcome back</h1>
            <p className="font-manrope text-sm text-paw-text-muted">Log in to manage your orders and prescriptions.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-paw-error/8 border border-paw-error/15 text-paw-error text-sm font-manrope rounded-xl px-4 py-3" data-testid="login-error">{error}</div>
            )}
            <div className="space-y-1.5">
              <Label className="font-manrope text-sm font-medium text-paw-text">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-11 rounded-xl border-paw-border bg-white focus:border-paw-primary focus:ring-paw-primary/20" data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="font-manrope text-sm font-medium text-paw-text">Password</Label>
                <Link to="/forgot-password" className="font-manrope text-xs font-medium text-paw-accent hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
              </div>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className="h-11 rounded-xl border-paw-border bg-white focus:border-paw-primary focus:ring-paw-primary/20" data-testid="login-password-input" />
            </div>
            <button type="submit" disabled={loading} className="w-full bg-paw-primary hover:bg-paw-primary-hover disabled:opacity-60 text-white rounded-xl h-11 font-manrope font-semibold text-sm transition-colors flex items-center justify-center gap-2" data-testid="login-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Log In <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <p className="text-center mt-8 font-manrope text-sm text-paw-text-muted">
            Don't have an account? <Link to="/register" className="text-paw-accent font-semibold hover:underline" data-testid="login-register-link">Create one</Link>
          </p>
        </div>
      </div>
      {/* Right, image */}
      <div className="hidden lg:block lg:w-[45%] relative">
        <img src={BG_IMG} alt="Relaxing dog" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-paw-primary/40 to-transparent" />
        <div className="absolute bottom-12 left-10 right-10">
          <p className="font-outfit font-bold text-2xl text-white mb-2">Affordable pet medication,<br />delivered to your door.</p>
          <p className="font-manrope text-sm text-white/70">Affordable pet meds delivered to your door. In partnership with Canadian pharmacies and veterinarians.</p>
        </div>
      </div>
    </div>
  );
}
